import { useEffect } from 'react';
import useSWR from 'swr';

import { FixMe } from '@utils/types';
import useStores from '@stores/useStores';
import { ApiGetResource, fetchData, swrConfigDefaults } from '@api';

import { AccountManagementStore } from '../store';

export const getResourceCacheKey = (
  workspaceId: string,
  resourceDefinitionName: string,
  apiResourceName: ApiGetResource,
) => `${workspaceId}-${resourceDefinitionName}-${apiResourceName}`;

export const useLoadAccountsIntoAccountManagementStore = ({
  accountManagementStore,
  legacyAccountsRole,
}: {
  accountManagementStore: AccountManagementStore;
  legacyAccountsRole?: string;
}) => {
  const { workspaceStore, messagesStore, accountDefinitionsListStore } = useStores();
  const {
    setAccountDefinitions,
    setAccounts,
    setLegacyAccounts,
    supportedAccountTypes,
    resourceDefinitionName,
  } = accountManagementStore;

  // Fetch accounts(migrated) only if the destination supports accounts types

  const { isLoading: loadingAccounts, accountsApiResponse } = (() => {
    if (supportedAccountTypes.length > 0) {
      const { isLoading, data: accountsApiResponse } = useSWR(
        getResourceCacheKey(workspaceStore.id, resourceDefinitionName, ApiGetResource.ACCOUNTS),
        fetchData({
          path: `/web/workspaces/${workspaceStore.id}`,
          resource: ApiGetResource.ACCOUNTS,
          errorMessage: 'Failed to fetch destination accounts',
          params: {
            definitionNames: supportedAccountTypes.join(','),
          },
          onError: (e) => messagesStore.showErrorMessage(e),
        }),
        swrConfigDefaults,
      );

      return { isLoading, accountsApiResponse };
    }

    return { isLoading: false, accountsApiResponse: undefined };
  })();

  // Fetch legacy accounts
  const path = `/workspaces/${workspaceStore.id}/destination/accounts/${legacyAccountsRole}`;

  const { isLoading: loadingLegacyAccounts, data: legacyAccountsApiResponse } = useSWR(
    legacyAccountsRole
      ? getResourceCacheKey(
          workspaceStore.id,
          resourceDefinitionName,
          ApiGetResource.ACCOUNTS_LEGACY,
        )
      : null,
    fetchData<ApiGetResource.ACCOUNTS_LEGACY>({
      path,
      errorMessage: 'Failed to fetch destination accounts',
      onError: (e) => messagesStore.showErrorMessage(e),
    }),
    swrConfigDefaults,
  );

  useEffect(() => {
    // Set account definitions from the store
    if (accountDefinitionsListStore.accountDefinitions.length > 0) {
      setAccountDefinitions(accountDefinitionsListStore.accountDefinitions);
    }

    if (accountsApiResponse) {
      setAccounts(accountsApiResponse.data);
    }
    if (legacyAccountsApiResponse) {
      setLegacyAccounts(legacyAccountsApiResponse as FixMe);
    }
  }, [
    accountsApiResponse,
    legacyAccountsApiResponse,
    accountDefinitionsListStore.accountDefinitions,
  ]);

  return {
    isLoading: loadingAccounts || loadingLegacyAccounts || !accountDefinitionsListStore.firstLoad,
  };
};
